/* ═══ A FAKE FIRESTORE FOR TESTS (docs/quotes/quote-saving.md) ═══
   The same shape as lib/dataService.js for the collections and the key-value store the
   quote builder writes, with Firestore's real merge rule: setDoc with merge:true
   merges nested maps, replaces arrays, keeps explicit nulls, and drops undefined.
   updateDoc fails on a missing document. A reload returns deep copies, as loadAll does,
   so a test can prove what a record looks like after a round trip.

   Shared by the vitest scenarios and the Playwright quotes fixture (which keeps the
   documents in sessionStorage so a page reload is a real reload). Failures and delays
   are injected per operation through `control`. */

import { checkRevision, mergeDoc } from "../lib/revisionCheck.js";

// One merge rule: the one dataService's checked save uses to hand back the committed
// document (lib/revisionCheck.js), so the fake can't drift from it.
export { mergeDoc };

const clone = (v) => (v === undefined ? undefined : JSON.parse(JSON.stringify(v)));

const stripUndefined = (obj) => JSON.parse(JSON.stringify(obj));

export function createFakeFirestore({ persist = null, initial = {} } = {}) {
  // path "collection/id" -> data
  const docs = new Map();
  const load = () => {
    const stored = persist?.load?.();
    // Persisted stores are [path, data] entries (a key-value document has no id field);
    // the seed is by collection.
    if (Array.isArray(stored)) { for (const [path, d] of stored) docs.set(path, clone(d)); return; }
    for (const [col, list] of Object.entries(stored || initial || {})) {
      for (const d of list || []) docs.set(`${col}/${d.id || d._docId}`, clone(d));
    }
  };
  load();
  const flush = () => { persist?.save?.([...docs.entries()].map(([p, d]) => [p, clone(d)])); };
  const dump = () => {
    const out = {};
    for (const [path, data] of docs.entries()) {
      const [col] = path.split("/");
      (out[col] ||= []).push(clone(data));
    }
    return out;
  };

  // Injected behaviour: control.before(op, path, data) may throw (the write fails) or
  // return a promise (the write waits). ops: "set", "update", "delete", "get", "kv.set"...
  const control = { before: null, log: [] };
  const gate = async (op, path, data) => {
    control.log.push({ op, path });
    if (control.before) await control.before(op, path, data);
  };

  const writable = (record) => stripUndefined(record);

  const collection = (name) => ({
    loadAll: async () => {
      await gate("loadAll", name);
      return [...docs.entries()].filter(([p]) => p.startsWith(`${name}/`)).map(([p, d]) => ({ ...clone(d), _docId: p.slice(name.length + 1) }));
    },
    get: async (id) => {
      try {
        await gate("get", `${name}/${id}`);
      } catch (e) {
        console.error(`get(${name}):`, e);
        return null;
      }
      const d = docs.get(`${name}/${id}`);
      return d ? { ...clone(d), _docId: id } : null;
    },
    // As dataService: the read that throws on failure, so a missing document (null) and a
    // failed read are told apart.
    fetch: async (id) => {
      await gate("fetch", `${name}/${id}`);
      const d = docs.get(`${name}/${id}`);
      return d ? { ...clone(d), _docId: id } : null;
    },
    save: async (record) => {
      const docId = record.id || `${name.slice(0, 2)}_${Date.now()}`;
      const path = `${name}/${docId}`;
      await gate("set", path, record);
      docs.set(path, mergeDoc(docs.get(path), { ...writable(record), updatedAt: new Date().toISOString() }));
      flush();
      return docId;
    },
    // The same check and the same keepFromStored rule as dataService.saveChecked, read at
    // write time (after `gate`), so a patch injected while a save waits is what the
    // transaction sees.
    saveChecked: async (record, expected, { keepFromStored = [] } = {}) => {
      const docId = record.id || `${name.slice(0, 2)}_${Date.now()}`;
      const path = `${name}/${docId}`;
      await gate("set", path, record);
      const existing = docs.get(path);
      checkRevision(!!existing, existing?.savedAt, expected);
      const payload = { ...writable(record), updatedAt: new Date().toISOString() };
      if (existing) for (const f of keepFromStored) delete payload[f];
      const committed = mergeDoc(existing, payload);
      docs.set(path, committed);
      flush();
      return { id: docId, record: { ...clone(committed), _docId: docId } };
    },
    patch: async (id, patch) => {
      const path = `${name}/${id}`;
      await gate("update", path, patch);
      if (!docs.has(path)) { const e = new Error(`No document to update: ${path}`); e.code = "not-found"; throw e; }
      // updateDoc: each field given replaces the stored one whole (a map value included;
      // only a dotted field path would reach inside one, and no caller writes those).
      docs.set(path, { ...docs.get(path), ...clone(writable(patch)), updatedAt: new Date().toISOString() });
      flush();
      return id;
    },
    delete: async (id) => {
      const path = `${name}/${id}`;
      await gate("delete", path);
      docs.delete(path);
      flush();
    },
    bulkSave: async (records) => { for (const r of records) await collection(name).save(r); return records.length; },
    bulkDelete: async (ids) => { for (const id of ids) await collection(name).delete(id); return ids.length; },
    subscribe: () => () => {},
  });

  const kv = {
    get: async (key) => {
      await gate("kv.get", `app/${key}`);
      const d = docs.get(`app/${key}`);
      return d ? JSON.parse(d.value) : null;
    },
    // As dataService: the read that throws on failure (op "kv.fetch").
    fetch: async (key) => {
      await gate("kv.fetch", `app/${key}`);
      const d = docs.get(`app/${key}`);
      return d ? JSON.parse(d.value) : null;
    },
    getRaw: async (key) => { await gate("kv.get", `app/${key}`); return clone(docs.get(`app/${key}`)) || null; },
    set: async (key, val) => {
      await gate("kv.set", `app/${key}`, val);
      docs.set(`app/${key}`, { value: JSON.stringify(val), updatedAt: new Date().toISOString() });
      flush();
    },
    delete: async (key) => {
      await gate("kv.delete", `app/${key}`);
      docs.delete(`app/${key}`);
      flush();
    },
  };

  const cols = {};
  const col = (name) => (cols[name] ||= collection(name));

  return {
    control,
    kv,
    quotes: col("quotes"),
    jobs: col("jobs"),
    contacts: col("contacts"),
    clients: col("clients"),
    collection: col,
    /** What a fresh page load would read: deep copies of every document, by collection. */
    reload: () => dump(),
    /** The raw stored document (deep copy), or undefined. */
    doc: (path) => clone(docs.get(path)),
    has: (path) => docs.has(path),
    size: () => docs.size,
  };
}
